import { useEffect, useState } from 'react';
import { useDeviceDetector } from './use-device-detector';

interface UseMobileDeepLinkOptions {
  deepLinkUrl: string;
  fallbackDelay?: number;
  onFallback?: () => void;
}

export const useMobileDeepLink = ({
  deepLinkUrl,
  fallbackDelay = 2000,
  onFallback,
}: UseMobileDeepLinkOptions) => {
  const [shouldShowFallback, setShouldShowFallback] = useState(false);
  const [isAttemptingDeepLink, setIsAttemptingDeepLink] = useState(false);
  const { isMobile } = useDeviceDetector();

  useEffect(() => {
    if (!isMobile || !deepLinkUrl) {
      setIsAttemptingDeepLink(false);
      setShouldShowFallback(false);
      return;
    }

    setIsAttemptingDeepLink(true);
    setShouldShowFallback(false);

    let fallbackTimer: NodeJS.Timeout;
    let cleanupTimer: NodeJS.Timeout;

    const attemptDeepLink = () => {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = deepLinkUrl;
      document.body.appendChild(iframe);

      const directLinkTimer = setTimeout(() => {
        try {
          window.location.href = deepLinkUrl;
        } catch (error) {
          console.warn('Deep link failed:', error);
        }
      }, 500);

      fallbackTimer = setTimeout(() => {
        setShouldShowFallback(true);
        setIsAttemptingDeepLink(false);
        onFallback?.();
      }, fallbackDelay);

      cleanupTimer = setTimeout(() => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
        clearTimeout(directLinkTimer);
      }, 1000);

      const handleVisibilityChange = () => {
        if (document.hidden) {
          clearTimeout(fallbackTimer);
          clearTimeout(cleanupTimer);
          clearTimeout(directLinkTimer);
          setIsAttemptingDeepLink(false);
          setShouldShowFallback(false);
        }
      };

      const handleBlur = () => {
        clearTimeout(fallbackTimer);
        clearTimeout(cleanupTimer);
        clearTimeout(directLinkTimer);
        setIsAttemptingDeepLink(false);
        setShouldShowFallback(false);
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('blur', handleBlur);

      return () => {
        clearTimeout(fallbackTimer);
        clearTimeout(cleanupTimer);
        clearTimeout(directLinkTimer);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('blur', handleBlur);
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      };
    };

    const cleanup = attemptDeepLink();

    return cleanup;
  }, [isMobile, deepLinkUrl, fallbackDelay, onFallback]);

  return {
    shouldShowFallback,
    isAttemptingDeepLink,
    isMobile,
  };
};
